import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Sliders,
  Shield,
  TrendingUp,
  Clock,
  Mail,
  Bell,
  Coins,
  AlertTriangle,
  Check,
  X,
  Loader2,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { HighGuardSettings } from './settings/HighGuardSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const SettingsModal = ({ open, onOpenChange, botStatus, onSettingsUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [editSettings, setEditSettings] = useState({
    // RSI Settings
    rsi_oversold: 25,
    rsi_overbought: 75,
    no_loss_sell_enabled: true,
    min_sell_profit_percent: 1.0,
    min_hold_minutes: 5,
    trade_percent: 10,
    min_trade: 1,
    // Concentration Limit
    concentration_limit_enabled: true,
    concentration_limit_percent: 50,
    // Stop-Loss & Take-Profit
    stop_loss_enabled: false,
    stop_loss_percent: 5,
    take_profit_enabled: false,
    take_profit_percent: 10,
    micro_profit_enabled: false,
    micro_profit_percent: 0.5,
    trailing_stop_enabled: false,
    trailing_stop_percent: 3,
    // Post-sell cooldown
    sell_cooldown_enabled: true,
    sell_cooldown_minutes: 1,
    // 60-day high buy guard
    high_guard_enabled: true,
    high_guard_percent: 10,
    high_guard_days: 60,
    // Schedule
    schedule_enabled: false,
    schedule_start_hour: 9,
    schedule_end_hour: 17,
    schedule_days: [0, 1, 2, 3, 4],
    // Email Alerts
    email_alerts_enabled: false,
    alert_email: '',
    alert_on_trade: true,
    alert_on_error: true,
    alert_on_bot_stop: true,
    daily_digest_enabled: true,
    weekly_digest_enabled: true,
    // Push Notifications
    push_enabled: false,
    // Trading Pairs
    active_pairs: ['BTC-USD'],
  });

  const [supportedPairs, setSupportedPairs] = useState(['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD']);
  const [testingEmail, setTestingEmail] = useState(false);

  // Fetch settings directly from API when modal opens
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_V2}/user/bot/status`, { withCredentials: true });
      const data = response.data;
      
      console.log('Fetched settings - stop_loss_enabled:', data.stop_loss_enabled, 'type:', typeof data.stop_loss_enabled);
      
      setEditSettings({
        rsi_oversold: data.rsi_oversold || 25,
        rsi_overbought: data.rsi_overbought || 75,
        no_loss_sell_enabled: data.no_loss_sell_enabled !== false,
        min_sell_profit_percent: data.min_sell_profit_percent ?? 1.0,
        min_hold_minutes: data.min_hold_minutes ?? 5,
        trade_percent: data.trade_percent || 10,
        min_trade: data.min_trade || 1,
        // Concentration Limit
        concentration_limit_enabled: data.concentration_limit_enabled !== false,
        concentration_limit_percent: data.concentration_limit_percent || 50,
        // Boolean values - handle explicitly to avoid false becoming undefined
        stop_loss_enabled: data.stop_loss_enabled === true,
        stop_loss_percent: data.stop_loss_percent || 5,
        take_profit_enabled: data.take_profit_enabled === true,
        take_profit_percent: data.take_profit_percent || 10,
        micro_profit_enabled: data.micro_profit_enabled === true,
        micro_profit_percent: data.micro_profit_percent || 0.5,
        trailing_stop_enabled: data.trailing_stop_enabled === true,
        trailing_stop_percent: data.trailing_stop_percent || 3,
        // Post-sell cooldown
        sell_cooldown_enabled: data.sell_cooldown_enabled !== false,
        sell_cooldown_minutes: data.sell_cooldown_minutes || 1,
        high_guard_enabled: data.high_guard_enabled !== false,
        high_guard_percent: data.high_guard_percent ?? 10,
        high_guard_days: data.high_guard_days ?? 60,
        schedule_enabled: data.schedule_enabled === true,
        schedule_start_hour: data.schedule_start_hour || 9,
        schedule_end_hour: data.schedule_end_hour || 17,
        schedule_days: data.schedule_days || [0, 1, 2, 3, 4],
        email_alerts_enabled: data.email_alerts_enabled === true,
        alert_email: data.alert_email || '',
        alert_on_trade: data.alert_on_trade !== false,
        alert_on_error: data.alert_on_error !== false,
        alert_on_bot_stop: data.alert_on_bot_stop !== false,
        daily_digest_enabled: data.daily_digest_enabled !== false,
        weekly_digest_enabled: data.weekly_digest_enabled !== false,
        push_enabled: data.push_enabled === true,
        active_pairs: Array.isArray(data.active_pairs) && data.active_pairs.length > 0 
          ? data.active_pairs 
          : ['BTC-USD'],
      });
      
      if (data.supported_pairs) {
        setSupportedPairs(data.supported_pairs);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch settings when modal opens
  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open, fetchSettings]);

  const handleSave = async () => {
    console.log('handleSave called, botStatus:', botStatus);
    
    if (botStatus === 'running') {
      toast.error('Stop the bot before changing settings');
      return;
    }

    if (editSettings.rsi_oversold >= editSettings.rsi_overbought) {
      toast.error('RSI Oversold must be less than RSI Overbought');
      return;
    }

    try {
      const payload = {
        rsi_oversold: editSettings.rsi_oversold,
        rsi_overbought: editSettings.rsi_overbought,
        no_loss_sell_enabled: editSettings.no_loss_sell_enabled,
        min_sell_profit_percent: editSettings.min_sell_profit_percent,
        min_hold_minutes: editSettings.min_hold_minutes,
        trade_percent: editSettings.trade_percent,
        min_trade_usd: editSettings.min_trade,
        concentration_limit_enabled: editSettings.concentration_limit_enabled,
        concentration_limit_percent: editSettings.concentration_limit_percent,
        stop_loss_enabled: editSettings.stop_loss_enabled,
        stop_loss_percent: editSettings.stop_loss_percent,
        take_profit_enabled: editSettings.take_profit_enabled,
        take_profit_percent: editSettings.take_profit_percent,
        micro_profit_enabled: editSettings.micro_profit_enabled,
        micro_profit_percent: editSettings.micro_profit_percent,
        trailing_stop_enabled: editSettings.trailing_stop_enabled,
        trailing_stop_percent: editSettings.trailing_stop_percent,
        sell_cooldown_enabled: editSettings.sell_cooldown_enabled,
        sell_cooldown_minutes: editSettings.sell_cooldown_minutes,
        high_guard_enabled: editSettings.high_guard_enabled,
        high_guard_percent: editSettings.high_guard_percent,
        high_guard_days: editSettings.high_guard_days,
        schedule_enabled: editSettings.schedule_enabled,
        schedule_start_hour: editSettings.schedule_start_hour,
        schedule_end_hour: editSettings.schedule_end_hour,
        schedule_days: editSettings.schedule_days,
        email_alerts_enabled: editSettings.email_alerts_enabled,
        alert_email: editSettings.alert_email,
        alert_on_trade: editSettings.alert_on_trade,
        alert_on_error: editSettings.alert_on_error,
        alert_on_bot_stop: editSettings.alert_on_bot_stop,
        daily_digest_enabled: editSettings.daily_digest_enabled,
        weekly_digest_enabled: editSettings.weekly_digest_enabled,
        push_enabled: editSettings.push_enabled,
        active_pairs: editSettings.active_pairs,
      };
      console.log('Saving settings payload:', payload);
      const response = await axios.post(`${API_V2}/user/settings/update`, payload, { withCredentials: true });
      console.log('Save response:', response.data);
      toast.success('Settings updated successfully');
      onOpenChange(false);
      if (onSettingsUpdated) onSettingsUpdated();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update settings', { description: error.response?.data?.detail || error.message });
    }
  };

  const handleTestEmail = async () => {
    if (!editSettings.alert_email) {
      toast.error('Please enter an email address first');
      return;
    }
    
    setTestingEmail(true);
    try {
      // First save the email
      await axios.post(`${API_V2}/user/settings/update`, {
        alert_email: editSettings.alert_email,
      }, { withCredentials: true });
      // Then send test
      await axios.post(`${API_V2}/user/alerts/test-email`, {}, { withCredentials: true });
      toast.success('Test email sent!');
    } catch (error) {
      toast.error('Failed to send test email', { description: error.response?.data?.detail });
    } finally {
      setTestingEmail(false);
    }
  };

  const toggleDay = (day) => {
    const days = editSettings.schedule_days;
    if (days.includes(day)) {
      setEditSettings({ ...editSettings, schedule_days: days.filter(d => d !== day) });
    } else {
      setEditSettings({ ...editSettings, schedule_days: [...days, day].sort() });
    }
  };

  const togglePair = (pair) => {
    const pairs = editSettings.active_pairs;
    if (pairs.includes(pair)) {
      if (pairs.length > 1) {
        setEditSettings({ ...editSettings, active_pairs: pairs.filter(p => p !== pair) });
      } else {
        toast.error('At least one trading pair must be active');
      }
    } else {
      setEditSettings({ ...editSettings, active_pairs: [...pairs, pair] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41] font-mono max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#00FF41] uppercase tracking-widest flex items-center gap-2">
            <Sliders className="h-5 w-5" strokeWidth={1.5} />
            ADVANCED SETTINGS
          </DialogTitle>
          <DialogDescription className="text-[#008F11]">
            Configure all trading parameters. Stop the bot before making changes.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#00FF41]" />
            <span className="ml-2">Loading settings...</span>
          </div>
        ) : (
        <Tabs defaultValue="trading" className="w-full">
          <TabsList className="grid grid-cols-5 bg-[#0a0a0a] border border-[#003B00]">
            <TabsTrigger value="trading" className="data-[state=active]:bg-[#003B00] text-xs">RSI</TabsTrigger>
            <TabsTrigger value="protection" className="data-[state=active]:bg-[#003B00] text-xs" data-testid="settings-tab-protection">PROTECTION</TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-[#003B00] text-xs">SCHEDULE</TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-[#003B00] text-xs">ALERTS</TabsTrigger>
            <TabsTrigger value="pairs" className="data-[state=active]:bg-[#003B00] text-xs">PAIRS</TabsTrigger>
          </TabsList>

          {/* RSI Trading Settings */}
          <TabsContent value="trading" className="space-y-4 mt-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-[#008F11] text-sm uppercase">RSI Oversold (BUY)</Label>
                <span className="text-[#00FF41] font-bold">{editSettings.rsi_oversold}</span>
              </div>
              <Slider
                value={[editSettings.rsi_oversold]}
                onValueChange={([value]) => setEditSettings({ ...editSettings, rsi_oversold: value })}
                min={10}
                max={50}
                step={1}
                className="[&_[role=slider]]:bg-[#00FF41]"
              />
              <p className="text-xs text-[#008F11]">Buy when RSI drops below this value (lower = more conservative)</p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-[#008F11] text-sm uppercase">RSI Overbought (SELL)</Label>
                <span className="text-red-500 font-bold">{editSettings.rsi_overbought}</span>
              </div>
              <Slider
                value={[editSettings.rsi_overbought]}
                onValueChange={([value]) => setEditSettings({ ...editSettings, rsi_overbought: value })}
                min={50}
                max={90}
                step={1}
                className="[&_[role=slider]]:bg-red-500"
              />
              <p className="text-xs text-[#008F11]">Sell when RSI rises above this value (higher = more conservative)</p>
            </div>

            <Separator className="bg-[#003B00]" />

            {/* No-Loss Sell Rule */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#00FF41]" />
                  <Label className="text-[#008F11] text-sm uppercase">No-Loss Sell Rule</Label>
                  <Badge className="bg-[#00FF41]/20 text-[#00FF41] text-xs">RECOMMENDED</Badge>
                </div>
                <Switch
                  checked={editSettings.no_loss_sell_enabled}
                  onCheckedChange={(checked) => setEditSettings({ ...editSettings, no_loss_sell_enabled: checked })}
                  className="data-[state=checked]:bg-[#00FF41]"
                />
              </div>
              <p className="text-xs text-[#008F11]">
                When enabled, RSI sell signals are only executed if the trade is profitable. 
                This prevents selling at a loss in choppy markets. Stop-loss still works normally.
              </p>
              
              {editSettings.no_loss_sell_enabled && (
                <div className="space-y-3 mt-3 pl-4 border-l-2 border-[#003B00]">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-[#008F11] text-xs uppercase">Min Profit to Sell</Label>
                      <span className="text-[#00FF41] font-bold text-sm" data-testid="min-sell-profit-value">{editSettings.min_sell_profit_percent}%</span>
                    </div>
                    <Slider
                      value={[editSettings.min_sell_profit_percent]}
                      onValueChange={([value]) => setEditSettings({ ...editSettings, min_sell_profit_percent: value })}
                      min={0.5}
                      max={10}
                      step={0.5}
                      className="w-full"
                      data-testid="min-sell-profit-slider"
                    />
                    <p className="text-xs text-[#008F11]/60">
                      Won't sell until profit reaches at least {editSettings.min_sell_profit_percent}% above buy price
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-[#008F11] text-xs uppercase">Min Hold Time</Label>
                      <span className="text-[#00FF41] font-bold text-sm" data-testid="min-hold-value">{editSettings.min_hold_minutes} min</span>
                    </div>
                    <Slider
                      value={[editSettings.min_hold_minutes]}
                      onValueChange={([value]) => setEditSettings({ ...editSettings, min_hold_minutes: value })}
                      min={1}
                      max={60}
                      step={1}
                      className="w-full"
                      data-testid="min-hold-slider"
                    />
                    <p className="text-xs text-[#008F11]/60">
                      After buying, won't sell same pair for at least {editSettings.min_hold_minutes} minutes
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Separator className="bg-[#003B00]" />

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-[#008F11] text-sm uppercase">Trade Size (%)</Label>
                <span className="text-[#00FF41] font-bold">{editSettings.trade_percent}%</span>
              </div>
              <Slider
                value={[editSettings.trade_percent]}
                onValueChange={([value]) => setEditSettings({ ...editSettings, trade_percent: value })}
                min={1}
                max={50}
                step={1}
                className="[&_[role=slider]]:bg-[#00FF41]"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#008F11] text-sm uppercase">Min Trade (USD)</Label>
              <Input
                type="number"
                value={editSettings.min_trade}
                onChange={(e) => setEditSettings({ ...editSettings, min_trade: parseFloat(e.target.value) || 1 })}
                className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41]"
              />
            </div>
          </TabsContent>

          {/* Protection Settings */}
          <TabsContent value="protection" className="space-y-4 mt-4">
            <HighGuardSettings editSettings={editSettings} setEditSettings={setEditSettings} />

            {/* Concentration Limit */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Shield className="h-4 w-4 text-yellow-500" />
                  <Label className="text-[#008F11] text-sm uppercase">Concentration Limit</Label>
                </div>
                <div className="flex-shrink-0">
                  <Switch
                    checked={editSettings.concentration_limit_enabled}
                    onCheckedChange={(checked) => setEditSettings({ ...editSettings, concentration_limit_enabled: checked })}
                    className="data-[state=checked]:bg-yellow-500"
                  />
                </div>
              </div>
              {editSettings.concentration_limit_enabled && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-[#008F11]">Max % of portfolio per coin</span>
                    <span className="text-yellow-500 font-bold">{editSettings.concentration_limit_percent}%</span>
                  </div>
                  <Slider
                    value={[editSettings.concentration_limit_percent]}
                    onValueChange={([value]) => setEditSettings({ ...editSettings, concentration_limit_percent: value })}
                    min={25}
                    max={100}
                    step={5}
                    className="[&_[role=slider]]:bg-yellow-500"
                  />
                  <p className="text-xs text-[#008F11]">
                    Prevents over-investing. Bot won't buy more of a coin if it's already {editSettings.concentration_limit_percent}% of your portfolio.
                  </p>
                </div>
              )}
            </div>

            {/* Stop-Loss */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Shield className="h-4 w-4 text-red-500" />
                  <Label className="text-[#008F11] text-sm uppercase">Stop-Loss</Label>
                </div>
                <div className="flex-shrink-0">
                  <Switch
                    checked={editSettings.stop_loss_enabled === true}
                    onCheckedChange={(checked) => {
                      console.log('Stop-loss toggle:', checked);
                      setEditSettings({ ...editSettings, stop_loss_enabled: checked });
                    }}
                    className="data-[state=checked]:bg-red-600"
                  />
                </div>
              </div>
              {editSettings.stop_loss_enabled === true && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-[#008F11]">Sell if price drops</span>
                    <span className="text-red-500 font-bold">{editSettings.stop_loss_percent}%</span>
                  </div>
                  <Slider
                    value={[editSettings.stop_loss_percent]}
                    onValueChange={([value]) => setEditSettings({ ...editSettings, stop_loss_percent: value })}
                    min={1}
                    max={20}
                    step={0.5}
                    className="[&_[role=slider]]:bg-red-500"
                  />
                </div>
              )}
            </div>

            {/* Take-Profit */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <TrendingUp className="h-4 w-4 text-[#00FF41]" />
                  <Label className="text-[#008F11] text-sm uppercase">Take-Profit</Label>
                </div>
                <div className="flex-shrink-0">
                  <Switch
                    checked={editSettings.take_profit_enabled === true}
                    onCheckedChange={(checked) => {
                      console.log('Take-profit toggle:', checked);
                      setEditSettings({ ...editSettings, take_profit_enabled: checked });
                    }}
                    className="data-[state=checked]:bg-[#00FF41]"
                  />
                </div>
              </div>
              {editSettings.take_profit_enabled === true && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-[#008F11]">Sell if profit reaches</span>
                    <span className="text-[#00FF41] font-bold">{editSettings.take_profit_percent}%</span>
                  </div>
                  <Slider
                    value={[editSettings.take_profit_percent]}
                    onValueChange={([value]) => setEditSettings({ ...editSettings, take_profit_percent: value })}
                    min={1}
                    max={50}
                    step={1}
                    className="[&_[role=slider]]:bg-[#00FF41]"
                  />
                </div>
              )}
            </div>

            {/* Micro-Profit (Quick Scalping) */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Zap className="h-4 w-4 text-cyan-400" />
                  <Label className="text-[#008F11] text-sm uppercase">Micro-Profit</Label>
                </div>
                <div className="flex-shrink-0">
                  <Switch
                    checked={editSettings.micro_profit_enabled === true}
                    onCheckedChange={(checked) => {
                      console.log('Micro-profit toggle:', checked);
                      setEditSettings({ ...editSettings, micro_profit_enabled: checked });
                    }}
                    className="data-[state=checked]:bg-cyan-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Quick scalping - sell when price rises X% above your average buy price. Great for volatile markets.
              </p>
              {editSettings.micro_profit_enabled === true && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-[#008F11]">Sell when up</span>
                    <span className="text-cyan-400 font-bold">{editSettings.micro_profit_percent}%</span>
                  </div>
                  <Slider
                    value={[editSettings.micro_profit_percent]}
                    onValueChange={([value]) => setEditSettings({ ...editSettings, micro_profit_percent: value })}
                    min={0.1}
                    max={5}
                    step={0.1}
                    className="[&_[role=slider]]:bg-cyan-400"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    ⚠️ Note: Coinbase fees are ~0.6% per trade. Set above 1.2% to ensure profit after round-trip fees.
                  </div>
                </div>
              )}
            </div>

            {/* Trailing Stop */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <Label className="text-[#008F11] text-sm uppercase">Trailing Stop</Label>
                </div>
                <div className="flex-shrink-0">
                  <Switch
                    checked={editSettings.trailing_stop_enabled === true}
                    onCheckedChange={(checked) => {
                      console.log('Trailing stop toggle:', checked);
                      setEditSettings({ ...editSettings, trailing_stop_enabled: checked });
                    }}
                    className="data-[state=checked]:bg-yellow-600"
                  />
                </div>
              </div>
              {editSettings.trailing_stop_enabled === true && (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-[#008F11]">Sell if drops from peak</span>
                    <span className="text-yellow-500 font-bold">{editSettings.trailing_stop_percent}%</span>
                  </div>
                  <Slider
                    value={[editSettings.trailing_stop_percent]}
                    onValueChange={([value]) => setEditSettings({ ...editSettings, trailing_stop_percent: value })}
                    min={1}
                    max={15}
                    step={0.5}
                    className="[&_[role=slider]]:bg-yellow-500"
                  />
                  <p className="text-xs text-[#008F11]">Follows price up, sells if it drops X% from highest point since buy</p>
                </div>
              )}
            </div>

          </TabsContent>

          {/* Schedule Settings */}
          <TabsContent value="schedule" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#00FF41]" />
                <Label className="text-[#008F11] text-sm uppercase">Trading Schedule (UTC)</Label>
              </div>
              <Switch
                checked={editSettings.schedule_enabled}
                onCheckedChange={(checked) => setEditSettings({ ...editSettings, schedule_enabled: checked })}
                className="data-[state=checked]:bg-[#00FF41]"
              />
            </div>

            {editSettings.schedule_enabled && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#008F11] text-xs">Start Hour (UTC)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={editSettings.schedule_start_hour}
                      onChange={(e) => setEditSettings({ ...editSettings, schedule_start_hour: parseInt(e.target.value) || 0 })}
                      className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#008F11] text-xs">End Hour (UTC)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={editSettings.schedule_end_hour}
                      onChange={(e) => setEditSettings({ ...editSettings, schedule_end_hour: parseInt(e.target.value) || 0 })}
                      className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#008F11] text-xs">Trading Days</Label>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <Button
                        key={day.value}
                        onClick={() => toggleDay(day.value)}
                        className={`px-3 py-1 text-xs rounded-none ${
                          editSettings.schedule_days.includes(day.value)
                            ? 'bg-[#00FF41] text-black'
                            : 'bg-[#0a0a0a] border border-[#003B00] text-[#008F11]'
                        }`}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Alerts Settings */}
          <TabsContent value="alerts" className="space-y-4 mt-4">
            {/* Email Alerts */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#00FF41]" />
                  <Label className="text-[#008F11] text-sm uppercase">Email Alerts</Label>
                </div>
                <Switch
                  checked={editSettings.email_alerts_enabled}
                  onCheckedChange={(checked) => setEditSettings({ ...editSettings, email_alerts_enabled: checked })}
                  className="data-[state=checked]:bg-[#00FF41]"
                />
              </div>

              {editSettings.email_alerts_enabled && (
                <>
                  <div className="space-y-2">
                    <Label className="text-[#008F11] text-xs">Email Address</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={editSettings.alert_email}
                        onChange={(e) => setEditSettings({ ...editSettings, alert_email: e.target.value })}
                        placeholder="your@email.com"
                        className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41] flex-1"
                      />
                      <Button
                        onClick={handleTestEmail}
                        disabled={testingEmail}
                        className="matrix-btn rounded-none text-xs"
                      >
                        {testingEmail ? 'SENDING...' : 'TEST'}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#008F11]">Alert on trades</span>
                    <Switch
                      checked={editSettings.alert_on_trade}
                      onCheckedChange={(checked) => setEditSettings({ ...editSettings, alert_on_trade: checked })}
                      className="data-[state=checked]:bg-[#00FF41]"
                      data-testid="toggle-alert-on-trade"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#008F11]">Alert on errors</span>
                    <Switch
                      checked={editSettings.alert_on_error}
                      onCheckedChange={(checked) => setEditSettings({ ...editSettings, alert_on_error: checked })}
                      className="data-[state=checked]:bg-[#00FF41]"
                      data-testid="toggle-alert-on-error"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#008F11]">Alert when bot stops</span>
                    <Switch
                      checked={editSettings.alert_on_bot_stop}
                      onCheckedChange={(checked) => setEditSettings({ ...editSettings, alert_on_bot_stop: checked })}
                      className="data-[state=checked]:bg-[#00FF41]"
                      data-testid="toggle-alert-on-bot-stop"
                    />
                  </div>

                  <Separator className="bg-[#003B00]" />

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#008F11]">Daily digest email</span>
                    <Switch
                      checked={editSettings.daily_digest_enabled}
                      onCheckedChange={(checked) => setEditSettings({ ...editSettings, daily_digest_enabled: checked })}
                      className="data-[state=checked]:bg-[#00FF41]"
                      data-testid="toggle-daily-digest"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#008F11]">Weekly digest email</span>
                    <Switch
                      checked={editSettings.weekly_digest_enabled}
                      onCheckedChange={(checked) => setEditSettings({ ...editSettings, weekly_digest_enabled: checked })}
                      className="data-[state=checked]:bg-[#00FF41]"
                      data-testid="toggle-weekly-digest"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Push Notifications */}
            <div className="p-4 border border-[#003B00] space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-[#00FF41]" />
                  <Label className="text-[#008F11] text-sm uppercase">Browser Notifications</Label>
                </div>
                <Switch
                  checked={editSettings.push_enabled}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      try {
                        if ('Notification' in window && Notification.requestPermission) {
                          Notification.requestPermission().then(permission => {
                            setEditSettings({ ...editSettings, push_enabled: true });
                            if (permission === 'granted') {
                              toast.success('Browser notifications enabled');
                            } else {
                              toast.info('Setting saved — browser may block notifications');
                            }
                          }).catch(() => {
                            setEditSettings({ ...editSettings, push_enabled: true });
                          });
                        } else {
                          setEditSettings({ ...editSettings, push_enabled: true });
                          toast.info('Setting saved — not supported on this browser');
                        }
                      } catch {
                        setEditSettings({ ...editSettings, push_enabled: true });
                      }
                    } else {
                      setEditSettings({ ...editSettings, push_enabled: false });
                    }
                  }}
                  className="data-[state=checked]:bg-[#00FF41]"
                />
              </div>
              <p className="text-xs text-[#008F11]">Get browser notifications for trades (keep tab open)</p>
            </div>
          </TabsContent>

          {/* Trading Pairs */}
          <TabsContent value="pairs" className="space-y-4 mt-4">
            <div className="flex items-center gap-2 mb-4">
              <Coins className="h-4 w-4 text-[#00FF41]" />
              <Label className="text-[#008F11] text-sm uppercase">Active Trading Pairs</Label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {supportedPairs.map((pair) => {
                const coin = pair.split('-')[0];
                const coinColors = {
                  'BTC': 'bg-orange-500/20',
                  'ETH': 'bg-blue-500/20',
                  'SOL': 'bg-purple-500/20',
                  'LTC': 'bg-gray-500/20',
                  'DOGE': 'bg-yellow-500/20',
                  'BCH': 'bg-green-500/20',
                  'XRP': 'bg-cyan-500/20',
                  'SHIB': 'bg-red-500/20'
                };
                const coinNames = {
                  'BTC': 'Bitcoin',
                  'ETH': 'Ethereum',
                  'SOL': 'Solana',
                  'LTC': 'Litecoin',
                  'DOGE': 'Dogecoin',
                  'BCH': 'Bitcoin Cash',
                  'XRP': 'Ripple',
                  'SHIB': 'Shiba Inu'
                };
                return (
                <div
                  key={pair}
                  onClick={() => togglePair(pair)}
                  className={`p-3 border cursor-pointer transition-all ${
                    editSettings.active_pairs.includes(pair)
                      ? 'border-[#00FF41] bg-[#00FF41]/10'
                      : 'border-[#003B00] hover:border-[#008F11]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${coinColors[coin] || 'bg-gray-500/20'}`}>
                        <span className="text-xs font-bold">{coin}</span>
                      </div>
                      <div>
                        <p className="font-bold text-sm">{coin}</p>
                        <p className="text-xs text-[#008F11]">{coinNames[coin] || coin}</p>
                      </div>
                    </div>
                    {editSettings.active_pairs.includes(pair) ? (
                      <Check className="h-5 w-5 text-[#00FF41]" />
                    ) : (
                      <X className="h-5 w-5 text-[#003B00]" />
                    )}
                  </div>
                </div>
              )})}
            </div>
            
            <p className="text-xs text-[#008F11] mt-2">Click to toggle. At least one pair must be active.</p>
          </TabsContent>
        </Tabs>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="matrix-btn rounded-none border-[#003B00]"
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="matrix-btn rounded-none bg-[#00FF41] text-black hover:bg-[#00FF41]/80"
            data-testid="settings-save-btn"
          >
            SAVE ALL SETTINGS
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
